import type { CategoryTreeNode } from '@ecom/contracts';

/**
 * The category tree as the rows of the Categories screen.
 *
 * The admin's tree endpoint returns every category in one answer — it is a
 * tree, and a tree cut into pages is not one — so a search here narrows what
 * is already on the screen rather than asking the server again.
 */

export interface CategoryRow {
  node: CategoryTreeNode;
  depth: number;
  /** False for a parent shown only because something beneath it matched — the path to the match. */
  matched: boolean;
}

/**
 * Depth-first rows, carrying depth so the screen can show the nesting. With a
 * search, the categories whose name (in either language) or slug contains it,
 * each under the parents that lead to it.
 */
export function categoryRows(nodes: readonly CategoryTreeNode[], search = ''): CategoryRow[] {
  const term = normalise(search);

  const walk = (list: readonly CategoryTreeNode[], depth: number): CategoryRow[] =>
    list.flatMap((node) => {
      const below = walk(node.children, depth + 1);
      if (!term) return [{ node, depth, matched: true }, ...below];

      const matched = names(node).some((name) => normalise(name).includes(term));
      return matched || below.length > 0 ? [{ node, depth, matched }, ...below] : [];
    });

  return walk(nodes, 0);
}

/** Everything a category is known by: its name as read, both stored names, and its slug. */
function names(node: CategoryTreeNode): string[] {
  return [node.name, node.slug, node.content?.name.en ?? '', node.content?.name.ar ?? ''];
}

function normalise(value: string): string {
  return value.trim().toLocaleLowerCase();
}
